<script setup>

import { ref, computed } from 'vue';

import use$T from 'cfg/useTranslation';
const $t = use$T();

import errors  from 'cfg/errorBag';

import { useAppStore } from 'store/store';
const { csrf } = useAppStore();

import MultiSelect from 'Common/MultiSelect/MultiSelect.vue';
import Contact from '@Contact/Contact';

const props = defineProps({
  reasons: { type: Array, default: ()=> [] },
});

function err(field) {
  return errors.first(field) || '';
}

const reasonOptions = computed(()=> props.reasons.map((r)=> ({
  value : r,
  label : $t('@form.reason_' + r),
})));

const form = ref({
  name    : '',
  email   : '',
  reason  : '',
  subject : '',
  message : '',
});

const loading = ref(false);
const status  = ref({ type: '', text: '' });

function setStatus(type, text) {
  status.value = { type, text };
}

async function submit() {
  if (loading.value) return;

  loading.value = true;
  errors.clear();
  setStatus('', '');

  const data = await Contact.sendMail({
    csrf    : csrf['contact::submit'],
    name    : form.value.name.trim(),
    email   : form.value.email.trim(),
    reason  : form.value.reason,
    subject : form.value.subject.trim(),
    message : form.value.message.trim(),
  }).finally(()=> loading.value = false);

  if (!data.success) {
    return setStatus('err', (data && data.message) || $t('@form.err_generic'));
  }
  setStatus('ok', data.message || $t('@form.err_generic'));
  form.value = { name: '', email: '', reason: '', subject: '', message: '' };
}

</script>

<template>
  <div class="contact-form-wrap">
    <h2>{{ $t('@form.h2') }}</h2>

    <div v-if="status.text" class="form-status" :class="status.type">{{ status.text }}</div>

    <form novalidate @submit.prevent="submit">

      <div class="form-row-2col">
        <div class="form-row">
          <label for="contact-name">{{ $t('@form.name_label') }}<span class="req">*</span></label>
          <input v-model="form.name" type="text" id="contact-name" autocomplete="name" :class="{ 'has-error': errors.has('name') }" @input="errors.remove('name')" :placeholder="$t('@form.name_placeholder')">
          <span v-if="err('name')" class="form-err">{{ err('name') }}</span>
        </div>
        <div class="form-row">
          <label for="contact-email">{{ $t('@form.email_label') }}<span class="req">*</span></label>
          <input v-model="form.email" type="email" id="contact-email" autocomplete="email" :class="{ 'has-error': errors.has('email') }" @input="errors.remove('email')" :placeholder="$t('@form.email_placeholder')">
          <span v-if="err('email')" class="form-err">{{ err('email') }}</span>
        </div>
      </div>

      <div class="form-row">
        <label>{{ $t('@form.reason_label') }}<span class="req">*</span></label>
        <MultiSelect v-model="form.reason"
          :options="reasonOptions"
          :title="$t('@form.reason_choose')"
          @update:modelValue="errors.remove('reason')"
        />
        <span v-if="err('reason')" class="form-err">{{ err('reason') }}</span>
      </div>

      <div class="form-row">
        <label for="contact-subject">{{ $t('@form.subject_label') }}</label>
        <input v-model="form.subject" type="text" id="contact-subject" :placeholder="$t('@form.subject_placeholder')">
      </div>

      <div class="form-row">
        <label for="contact-message">{{ $t('@form.message_label') }}<span class="req">*</span></label>
        <textarea v-model="form.message" id="contact-message" :class="{ 'has-error': errors.has('message') }" @input="errors.remove('message')" :placeholder="$t('@form.message_placeholder')"></textarea>
        <span v-if="err('message')" class="form-err">{{ err('message') }}</span>
      </div>

      <vue-ladda
        type="submit"
        data-style="expand-right"
        class="btn form-submit"
        :loading="loading"
      >
        {{ $t('@form.submit') }}
      </vue-ladda>

      <p class="form-note">{{ $t('@form.note') }}</p>

    </form>
  </div>
</template>
